import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Steps } from "primereact/steps";
import { MenuItem } from "primereact/menuitem";
import { Property } from "../../../interface/tracker";
import { AddPropertyContext } from "./AddPropertyContext";
import Summary from "./Summary";
import PurchaseAndLoans from "./PurchaseAndLoans";
import RentAndExpenses from "./RentAndExpenses";
import TaxAndMarket from "./TaxAndMarket";
import Review from "./Review";
import Results from "./Results";
import { REIDialoge } from "../../../components/ui";
import { IUserProfileDetails } from "../../../interface/userdetails";
import { useHttpGets } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { MEMBER_KEY, USER_DETAIL } from "../../../utils/constants";
import MyDefaultProfile from "../../Search/Child/defaultProfile";
import { useLocation, useNavigate } from "react-router-dom";
import { convertToMMDDYYYY } from "../../../utils/commonUtil";

interface AddPropertyProps {
  portfolioTrackerId?: number;
  onClose: (portfolioTrackerId?: number) => void;
}

const EditProperty = (props: AddPropertyProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [useDefault, setUseDefault] = useState(false);
  const [userProfile, setUserProfile] = useState<IUserProfileDetails | undefined>(undefined);
  const memberDetail = useMemo(() => JSON.parse(localStorage.getItem(USER_DETAIL) || "{}"), []);
  const memberName = `${memberDetail.FirstName} ${memberDetail.LastName}`;
  const [showDefaultProfile, setShowDefaultProfile] = useState<boolean>(false);
  const [reloadProfile, setReloadProfile] = useState<boolean>(false);
  const [portfolioTrackerId, setPortfolioTrackerId] = useState<number | undefined>(
    props.portfolioTrackerId ? props.portfolioTrackerId : undefined
  );
  const [property, setProperty] = useState<Partial<Property>>({});
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const newState = {
      ...location.state,
      tab: undefined,
      EditProperty: false,
    };

    navigate(location.pathname, {
      replace: true,
      state: newState,
    });
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const condition = useDefault
        ? `?ModulesId=${localStorage.getItem(MEMBER_KEY)}&Country=${memberDetail.Country}&memberName=${memberName}`
        : `/${memberDetail.Country}`;

      try {
        const result = await useHttpGets(`${ApiEndPoints.analyser}/UserDefault${condition}`);
        if (result.status === 200 && result.data) {
          setUserProfile(result.data as IUserProfileDetails);
        }
      } catch {
        console.error("Error fetching user profile.");
      }
    };

    fetchUserProfile();
  }, [useDefault, reloadProfile]);

  useEffect(() => {
    if (portfolioTrackerId) {
      const fetchPropertyDetails = async () => {
        try {
          const response = await useHttpGets(
            `${ApiEndPoints.tracker}/PortfolioTracker/Tracker?MemberId=${localStorage.getItem(
              MEMBER_KEY
            )}&PortfolioTrackerId=${portfolioTrackerId}&userCountry=${memberDetail.Country}`
          );
          if (response.status === 200 && response.data !== null) {
            const propertyDetails = response.data as Property;
            const formattedLoans = propertyDetails.ExistingLoans?.map((loan) => ({
              ...loan,
              Date: loan.Date ? convertToMMDDYYYY(loan.Date) : "",
              InterestRateReviewDate: loan.InterestRateReviewDate ? convertToMMDDYYYY(loan.InterestRateReviewDate) : "",
            }));
            setProperty({ ...propertyDetails, ExistingLoans: formattedLoans });
          }
        } catch (error) {
          console.error("Error fetching property details:", error);
        }
      };
      fetchPropertyDetails();
    }
  }, [portfolioTrackerId, reloadProfile]);

  const hideDefaultProfile = useCallback(async () => {
    setShowDefaultProfile(false);
    setReloadProfile((prev) => !prev);
  }, []);

  const items: MenuItem[] = [
    { label: "Summary" },
    { label: "Purchase & loans" },
    { label: "Rent & expenses" },
    { label: "Tax & market" },
    { label: "Review" },
    { label: "Results" },
  ];

  const handleNext = () => {
    setActiveIndex((prevActiveIndex) => prevActiveIndex + 1);
  };

  const closeAddProperty = () => {
    props.onClose(portfolioTrackerId ? portfolioTrackerId : 0);
  };

  const handleBack = () => {
    setActiveIndex((prevActiveIndex) => prevActiveIndex - 1);
  };

  const renderStepContent = () => {
    switch (activeIndex) {
      case 0:
        return <Summary onNext={handleNext} isEditMode={true} />;
      case 1:
        return <PurchaseAndLoans onNext={handleNext} onBack={handleBack} isEditMode={true} />;
      case 2:
        return <RentAndExpenses onNext={handleNext} onBack={handleBack} isEditMode={true} />;
      case 3:
        return (
          <TaxAndMarket
            onNext={handleNext}
            onBack={handleBack}
            portfolioTrackerId={portfolioTrackerId}
            isEditMode={true}
          />
        );
      case 4:
        return (
          <Review
            onNext={closeAddProperty}
            onBack={handleBack}
            portfolioTrackerId={portfolioTrackerId}
            isEditable={true}
          />
        );
      case 5:
        return <Results onBack={handleBack} />;
      default:
        return <div>Step {activeIndex + 1}</div>;
    }
  };

  const handleOnSelect = (index: number) => {
    setActiveIndex(index);

    if (index === 5) {
      closeAddProperty();
    }
  };

  return (
    <AddPropertyContext.Provider value={{ property, setProperty, userProfile, setUserProfile }}>
      <div className="p-card p-5">
        <REIDialoge
          showHeader={true}
          header={`Edit Default Profile`}
          visible={showDefaultProfile}
          handleEditClick={hideDefaultProfile}
          position="top"
          style={{ width: "60vw" }}
          customClass="edit-default-profile"
          contentComponent={<MyDefaultProfile closeDialog={hideDefaultProfile} />}
        ></REIDialoge>
        <div className="flex justify-content-between">
          <h2 className="text-2xl font-bold">Portfolio Tracker – Edit a Property</h2>
        </div>

        <div className="p-grid">
          <div className="p-col-12">
            <Steps
              model={items}
              activeIndex={activeIndex}
              onSelect={(e) => {
                handleOnSelect(e.index);
              }}
              readOnly={false}
            />
          </div>
        </div>

        <div className="p-mt-5 mt-4">{renderStepContent()}</div>
      </div>
    </AddPropertyContext.Provider>
  );
};

export default EditProperty;
